import { useState } from "react";
import { Button, Color, Flex, Icon, Text } from "src/elements";
import { IconType } from "src/elements/Icon";
import { Translate, useTranslation } from "src/i18n";
import { ApplyCounts, countApplyResults } from "../store/followUp";
import { AgentApplyItemResult, PogAgentRunResponse } from "../store/types";

const MAX_COLLAPSED_ROWS = 6;

type T = Translate<"pogAgent">;

const statusStyle = (
  status: AgentApplyItemResult["status"],
  t: T,
): { icon: IconType; color: string; label: string } => {
  if (status === "written") {
    return { icon: "checkmarkCircle", color: Color.primary, label: t("results.written") };
  }

  if (status === "new-created") {
    return { icon: "plus", color: Color.primary, label: t("results.newCreated") };
  }

  if (status === "skipped") {
    return { icon: "info", color: Color.textSecondary, label: t("results.skipped") };
  }

  return { icon: "alert", color: Color.red, label: t("results.failed") };
};

/** "5 written, 1 skipped" — only the statuses that occurred, written always first. */
export const describeCounts = (counts: ApplyCounts, t: T): string =>
  [
    t("results.countWritten", { count: counts.written }),
    counts.newCreated > 0 && t("results.countNewCreated", { count: counts.newCreated }),
    counts.skipped > 0 && t("results.countSkipped", { count: counts.skipped }),
    counts.failed > 0 && t("results.countFailed", { count: counts.failed }),
  ]
    .filter((part): part is string => Boolean(part))
    .join(", ");

interface Props {
  response: PogAgentRunResponse;
}

export const AgentApplyResults = ({ response }: Props) => {
  const { t } = useTranslation("pogAgent");
  const [isExpanded, setIsExpanded] = useState(false);

  if (!response.results.length) return null;

  const counts = countApplyResults(response);
  // A single-POG apply already says everything in the message and the headline.
  const isSingle = counts.total === 1;
  const visible = isExpanded ? response.results : response.results.slice(0, MAX_COLLAPSED_ROWS);

  return (
    <Flex column gap="4px">
      {!isSingle && (
        <Text variant="small1">
          {t("results.summary", { parts: describeCounts(counts, t), total: counts.total })}
        </Text>
      )}

      {visible.map((result) => {
        const style = statusStyle(result.status, t);

        return (
          <Flex key={`${result.target.pogId}-${result.target.storeCode}`} column gap="2px">
            <Flex gap="6px" align="center">
              <Icon name={style.icon} color={style.color} size={12} />

              <Text variant="small2" color={Color.textMain}>
                {t("results.store", { storeCode: result.target.storeCode })}
              </Text>

              <Text variant="small2" color={style.color}>
                {style.label}
              </Text>
            </Flex>

            {result.error && (
              <Text variant="small2" color={Color.red} wordBreak="break-word">
                {result.error}
              </Text>
            )}
          </Flex>
        );
      })}

      {response.results.length > MAX_COLLAPSED_ROWS && (
        <Flex>
          <Button size="small" variant="borderless" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded
              ? t("results.showLess")
              : t("results.showAll", { count: response.results.length })}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
